export const translations = {
    en: {
        nav: {
            products: "Products",
            services: "Services",
            pricing: "Pricing",
            faq: "FAQ",
            contact: "Contact",
            dashboard: "Dashboard",
            logout: "Logout",
            login: "Login",
            signup: "Sign Up"
        },
        hero: {
            title: "Unlock instant document intelligence with ",
            titleHighlight: "Corpusio",
            subtitle: "Your enterprise AI powerhouse",
            description: "Corpusio is the AI-powered, secure platform that turns scattered files into immediate answers. Ask naturally, search globally, and access documents through WhatsApp, web, or mobile—without the hassle.",
            cta: "Start Your Free Trial",
            secondaryCta: "Book a Demo"
        },
        problem: {
            title: "The ",
            titleHighlight: "Problem",
            text: "Information is everywhere, but ",
            textHighlight: "answers are hidden",
            textEnd: ". Employees waste hours searching, remote users get locked out, and security risks threaten compliance.",
            boldText: "Don't let chaos block your productivity."
        },
        solution: {
            title: "Our ",
            titleHighlight: "Solution",
            tagline: "Corpusio: Gateway to Your Corpus",
            subtext: "AI-driven SaaS platform delivers seamless, secure access to documents from anywhere—empowering teams to work smarter.",
            features: {
                naturalLanguage: {
                    title: "Natural Language Search",
                    description: "Ask questions, get instant answers with AI that understands context—not just keywords."
                },
                whatsapp: {
                    title: "WhatsApp Integration",
                    description: "Query your entire document library from WhatsApp. Fast, easy, mobile-friendly access."
                },
                multiformat: {
                    title: "Multi-format & Multilingual",
                    description: "Supports PDFs, Word, Excel, images, presentations, audio, and video. Works in all major languages."
                },
                security: {
                    title: "Enterprise Security",
                    description: "End-to-end encryption, role-based access, zero API key risks, and compliance-ready architecture."
                },
                setup: {
                    title: "Effortless Setup",
                    description: "Rapid onboarding, connects to your existing drives, no infrastructure headache."
                }
            }
        },
        features: {
            title: "Key ",
            titleHighlight: "Features",
            list: {
                aiSearch: { title: "Advanced AI Search", description: "Rapidly retrieve relevant content using cutting-edge RAG (Retrieval-Augmented Generation) technology." },
                cloudArch: { title: "Scalable Cloud Architecture", description: "Handle thousands to millions of documents with instant performance." },
                integrations: { title: "Custom Integrations", description: "Works with Microsoft 365, SharePoint, Google Workspace, Dropbox, and other business systems." },
                analytics: { title: "Analytics & Reporting", description: "Understand usage, optimize workflows, and prove ROI with built-in analytics." },
                fieldFriendly: { title: "Field-Friendly", description: "Mobile access and WhatsApp endpoint for true on-the-go intelligence." }
            }
        },
        whyChoose: {
            title: "Why Choose ",
            titleHighlight: "Corpusio?",
            benefits: {
                productivity: { title: "Boost Productivity", description: "faster workflows, 200+ hours saved monthly" },
                unlock: { title: "Unlock Answers", description: "Stop searching, start finding—your documents work for you" },
                security: { title: "Security You Can Trust", description: "Compliant with GDPR, HIPAA, SOC 2; encryption, audit trails, and isolated cloud storage" },
                support: { title: "Support That's Always On", description: "Standard, priority, and 24/7 support according to your plan" }
            }
        },
        services: {
            title: "Our ",
            titleHighlight: "Services",
            subtext: "Comprehensive support and services to ensure your success with Corpusio",
            list: {
                aiImpl: { title: "AI Implementation", description: "Custom AI solution deployment tailored to your business needs, including training and integration with existing systems." },
                sysInt: { title: "System Integration", description: "Seamless integration with Microsoft 365, SharePoint, Google Workspace, Dropbox, and other enterprise platforms." },
                analytics: { title: "Analytics & Reporting", description: "Comprehensive analytics dashboards and custom reports to track usage, ROI, and optimize workflows." },
                training: { title: "Training & Support", description: "End-to-end training programs for your team with ongoing technical support and documentation." },
                security: { title: "Security Auditing", description: "Regular security assessments, compliance auditing, and best practices implementation for data protection." },
                customDev: { title: "Custom Development", description: "Bespoke feature development and customizations to meet your unique business requirements." }
            }
        },
        products: {
            title: "Our Products",
            description: "Explore our AI-powered solutions for knowledge access, multilingual communication, creative media, fitness, and vehicle care",
            convoBuddy: {
                tag: "AI Companion",
                title: "ConvoBuddy: Multilingual AI Chat & Voice",
                description1: "ConvoBuddy is a multilingual AI companion that unifies AI chat, voice automation, and workflow orchestration for enterprises.",
                description2: "Perfect for customer support, sales teams, and operational workflows, ensuring consistent, intelligent communication in any language.",
                list: [
                    "Multilingual AI Chat & Voice",
                    "WhatsApp Business Integration",
                    "Voice Message Automation",
                    "Workflow Orchestration",
                    "Customer Insights & Analytics",
                    "Multi-channel Support"
                ],
                link: "Learn more about ConvoBuddy →",
                demo: {
                    q1: "What are your business hours?",
                    a1: "We're open Monday-Friday, 9 AM to 6 PM. How can I assist you today?",
                    q2: "Can you help in Spanish?",
                    a2: "¡Por supuesto! Puedo ayudarte en español. ¿En qué puedo asistirte?"
                }
            },
            melodyCrafters: {
                tag: "Music Creation",
                title: "MelodyCrafters: AI-Powered Music Composition",
                description1: "MelodyCrafters is an intelligent music composition platform that helps artists, producers, and content creators generate professional-quality melodies and lyrics using AI.",
                description2: "Whether you're writing a hit song, background music, or jingles, MelodyCrafters provides the tools and inspiration you need.",
                list: [
                    "AI-powered melody generation",
                    "Lyric writing assistance",
                    "Multiple music genres",
                    "Chord progression suggestions",
                    "Export to MIDI and Audio",
                    "Collaboration features"
                ],
                link: "Explore MelodyCrafters →"
            },
            happyFit: {
                tag: "Fitness & Wellness",
                title: "HappyFit: AI Personal Fitness Coach",
                description1: "HappyFit is an AI-powered fitness and wellness platform that creates personalized workout plans, tracks your progress, and provides real-time form corrections.",
                description2: "Get healthier with intelligent coaching that adapts to your fitness level, goals, and preferences.",
                list: [
                    "AI-powered workout plans",
                    "Real-time form correction",
                    "Progress tracking & analytics",
                    "Nutrition recommendations",
                    "Community challenges",
                    "Wearable device integration"
                ],
                link: "Start your fitness journey →"
            },
            rideWithDash: {
                tag: "Vehicle Care",
                title: "RideWithDash: AI vehicle companion for WhatsApp and web",
                description1: "RideWithDash helps drivers manage vehicle questions, diagnostics, garage history, and documents from WhatsApp and a clean web workspace.",
                description2: "Dash turns symptoms and photos into guided checks, work plans, AI vehicle reports, and a digital glovebox for registrations, insurance, uploads, and service records.",
                list: [
                    "WhatsApp and web vehicle assistant",
                    "AI diagnostics and guided troubleshooting",
                    "Garage profiles with reliability and work plans",
                    "Digital Glovebox for uploads and AI reports",
                    "Maintenance history and service-ready vehicle reports"
                ],
                link: "Explore RideWithDash →",
                ui: {
                    chatKicker: "WhatsApp + web",
                    chatTitle: "Dash chat",
                    chatMeta: "Verified WhatsApp identity",
                    userBubble: "My car clicks but will not start.",
                    answerTitle: "Dash diagnostic",
                    answer: "Check battery strength, starter click pattern, and dim headlights before adding this to your work plan.",
                    video: "Watch diagnostic video",
                    prompt: "Give Dash a photo or ask a question...",
                    garageKicker: "Member garage",
                    garageTitle: "Your Collection",
                    vehicles: [
                        { name: "2019 Ford", model: "Fiesta", plans: "3 plans", vin: "Missing" },
                        { name: "2020 Toyota", model: "Camry", plans: "3 plans", vin: "Missing" },
                        { name: "1988 Ford", model: "Lnt9000", plans: "0 plans", vin: "Verified" }
                    ],
                    vinStatus: "VIN status",
                    reliability: "Reliability",
                    gloveboxKicker: "Files + AI reports",
                    gloveboxTitle: "Digital Glovebox",
                    tabs: ["Uploaded Files", "AI Reports"],
                    files: ["vehicle_report_9", "vehicle_report_8", "vehicle_report_7"],
                    reportTitle: "Vehicle report",
                    reportVehicle: "2019 Ford Fiesta",
                    reportStatus: "Needs maintenance",
                    reportSummary: "Open diagnostics, work plan, service history, and parts in one place."
                }
            }
        },
        pricing: {
            title: "Pricing ",
            titleHighlight: "Plans",
            subtext: "Transparent, Flexible, & Scalable",
            period: "/mo",
            getStarted: "Get Started",
            plans: {
                standard: {
                    name: "Standard",
                    features: ["CPU instance", "5,000 messages", "WhatsApp & OpenAI", "PDF, DOCX, Excel, images", "12h response time"]
                },
                pro: {
                    name: "Pro",
                    features: ["Scaled CPU", "20,000 messages", "Priority & analytics", "All Standard formats", "+ mp3, wav", "6h response time"]
                },
                enterprise: {
                    name: "Enterprise",
                    features: ["NVIDIA GPU", "Unlimited messages", "Advanced AI, custom integrations", "All Pro formats", "+ mp4, mpg", "4h response, dedicated support", "24/7 phone support"]
                }
            },
            note: "All plans include ",
            noteHighlight1: "top-tier security",
            noteMiddle: " and mandatory ",
            noteHighlight2: "Business Associate Agreements",
            noteEnd: "."
        },
        useCases: {
            title: "Use ",
            titleHighlight: "Cases",
            description: "Legal, compliance, healthcare, sales, HR, engineering, fieldwork—any sector with serious document volume and a need for reliable answers.",
            stats: {
                retrieval: "faster retrieval",
                saved: "hours saved monthly"
            },
            footer: "Real-world customer results: Major time savings and improved client satisfaction"
        },
        security: {
            title: "Security & ",
            titleHighlight: "Compliance",
            intro: "Trust Corpusio for ",
            introHighlight: "enterprise-grade protection",
            features: {
                encrypted: "Encrypted storage and transmission",
                access: "Granular access control",
                audit: "Real-time audit trails",
                compliance: "GDPR, HIPAA, SOC 2 compliance"
            }
        },
        faq: {
            title: "Frequently Asked ",
            titleHighlight: "Questions",
            subtext: "Find answers to common questions about Corpusio",
            list: {
                q1: { q: "What is Corpusio?", a: "Corpusio is an AI-powered document intelligence platform that transforms scattered files into immediate answers. It enables natural language search across all your documents through WhatsApp, web, or mobile applications." },
                q2: { q: "How does WhatsApp integration work?", a: "Corpusio integrates seamlessly with WhatsApp Business, allowing you to query your entire document library directly through WhatsApp messages. Simply send your question, and our AI retrieves relevant information instantly." },
                q3: { q: "What file formats are supported?", a: "Corpusio supports a wide range of formats including PDFs, Word documents, Excel spreadsheets, images (JPG, PNG), presentations (PPT, PPTX), and even audio/video files (mp3, wav, mp4, mpg) in higher-tier plans." },
                q4: { q: "Is my data secure?", a: "Absolutely. Corpusio employs end-to-end encryption, role-based access controls, real-time audit trails, and is compliant with GDPR, HIPAA, and SOC 2 standards. Your data is stored in isolated cloud environments with zero API key risks." },
                q5: { q: "How long does setup take?", a: "Setup is remarkably fast—typically just a few minutes. Simply connect your storage (Google Drive, SharePoint, Dropbox, etc.), index your documents, and start searching. No special training or infrastructure changes required." },
                q6: { q: "Can I integrate Corpusio with my existing systems?", a: "Yes! Corpusio offers custom integrations with Microsoft 365, SharePoint, Google Workspace, Dropbox, and other business systems. Our Enterprise plan includes advanced AI and custom integration support." },
                q7: { q: "What kind of support do you offer?", a: "Support varies by plan: Standard (12h response), Pro (6h priority response with analytics), and Enterprise (4h response with dedicated support and 24/7 phone support)." },
                q8: { q: "Do you offer a free trial?", a: "Yes, we offer a free trial so you can experience Corpusio's capabilities firsthand. Contact us to get started with your trial today." }
            }
        },
        contact: {
            title: "Contact ",
            titleHighlight: "Us",
            subtext: "Get in touch with our team for inquiries, support, or partnerships",
            info: {
                email: "Email",
                phone: "Phone",
                hours: "Business Hours",
                hoursText: ["Monday - Friday: 9:00 AM - 6:00 PM IST", "Saturday: 10:00 AM - 4:00 PM IST", "Sunday: Closed"]
            },
            form: {
                name: "Name *",
                email: "Email *",
                phone: "Phone",
                code: "Code",
                country: "Country",
                selectCountry: "Select Country",
                state: "State",
                selectState: "Select State",
                city: "City",
                selectCity: "Select City",
                company: "Company",
                message: "Message *",
                send: "Send Message",
                sending: "Sending...",
                success: "✓ Message sent successfully! We'll get back to you within 24 hours.",
                error: "✗ Failed to send message. Please try again or email us directly.",
                fillRequired: "Please fill in all required fields"
            }
        },
        cta: {
            title: "Get ",
            titleHighlight: "Started",
            text: "Connect your storage, index your documents, and start searching—all in minutes.",
            accentText: "No training required.",
            question: "Ready to transform your organization's knowledge access?",
            buttons: {
                try: "Try Corpusio",
                bookDemo: "Book a Demo",
                contact: "Contact Us"
            },
            footer: "Corpusio is developed by industry leaders in AI and secure communications—trusted worldwide for transforming how enterprises unlock their knowledge."
        },
        footer: {
            description: "Unlock instant document intelligence with enterprise AI",
            products: "Product",
            features: "Features",
            pricing: "Pricing",
            security: "Security",
            integrations: "Integrations",
            company: "Company",
            about: "About Us",
            contact: "Contact",
            careers: "Careers",
            blog: "Blog",
            legal: "Legal",
            privacy: "Privacy Policy",
            terms: "Terms of Service",
            cookie: "Cookie Policy",
            compliance: "Compliance",
            copyright: "© 2026 Corpusio. All rights reserved."
        }
    },
    fr: {
        nav: {
            products: "Produits",
            services: "Services",
            pricing: "Tarification",
            faq: "FAQ",
            contact: "Contact",
            dashboard: "Tableau de bord",
            logout: "Déconnexion",
            login: "Connexion",
            signup: "S'inscrire"
        },
        hero: {
            title: "Libérez l'intelligence documentaire instantanée avec ",
            titleHighlight: "Corpusio",
            subtitle: "Votre puissance IA d'entreprise",
            description: "Corpusio est la plateforme sécurisée alimentée par l'IA qui transforme les fichiers dispersés en réponses immédiates. Posez des questions naturellement, recherchez globalement et accédez aux documents via WhatsApp, le web ou le mobile—sans tracas.",
            cta: "Commencez Votre Essai Gratuit",
            secondaryCta: "Réservez une Démo"
        },
        problem: {
            title: "Le ",
            titleHighlight: "Problème",
            text: "L'information est partout, mais ",
            textHighlight: "les réponses sont cachées",
            textEnd: ". Les employés perdent des heures à chercher, les utilisateurs distants sont bloqués et les risques de sécurité menacent la conformité.",
            boldText: "Ne laissez pas le chaos bloquer votre productivité."
        },
        solution: {
            title: "Notre ",
            titleHighlight: "Solution",
            tagline: "Corpusio : Passerelle vers Votre Corpus",
            subtext: "La plateforme SaaS pilotée par l'IA offre un accès transparent et sécurisé aux documents de n'importe où—permettant aux équipes de travailler plus intelligemment.",
            features: {
                naturalLanguage: {
                    title: "Recherche en Langage Naturel",
                    description: "Posez des questions, obtenez des réponses instantanées avec une IA qui comprend le contexte—pas seulement des mots-clés."
                },
                whatsapp: {
                    title: "Intégration WhatsApp",
                    description: "Interrogez toute votre bibliothèque de documents depuis WhatsApp. Rapide, facile, accès mobile."
                },
                multiformat: {
                    title: "Multi-format & Multilingue",
                    description: "Prend en charge les PDF, Word, Excel, images, présentations, audio et vidéo. Fonctionne dans toutes les langues principales."
                },
                security: {
                    title: "Sécurité d'Entreprise",
                    description: "Chiffrement de bout en bout, accès basé sur les rôles, zéro risque de clé API et architecture prête pour la conformité."
                },
                setup: {
                    title: "Configuration Sans Effort",
                    description: "Intégration rapide, se connecte à vos disques existants, pas de maux de tête d'infrastructure."
                }
            }
        },
        features: {
            title: "Principales ",
            titleHighlight: "Fonctionnalités",
            list: {
                aiSearch: { title: "Recherche IA Avancée", description: "Récupérez rapidement du contenu pertinent grâce à la technologie RAG de pointe." },
                cloudArch: { title: "Architecture Cloud Évolutive", description: "Gérez des milliers à des millions de documents avec des performances instantanées." },
                integrations: { title: "Intégrations Personnalisées", description: "Fonctionne avec Microsoft 365, SharePoint, Google Workspace, Dropbox et d'autres systèmes." },
                analytics: { title: "Analytique & Rapports", description: "Comprenez l'utilisation, optimisez les flux de travail et prouvez le ROI avec des analyses intégrées." },
                fieldFriendly: { title: "Adapté au Terrain", description: "Accès mobile et point de terminaison WhatsApp pour une véritable intelligence en déplacement." }
            }
        },
        whyChoose: {
            title: "Pourquoi Choisir ",
            titleHighlight: "Corpusio ?",
            benefits: {
                productivity: { title: "Booster la Productivité", description: "flux de travail plus rapides, plus de 200 heures économisées par mois" },
                unlock: { title: "Débloquez les Réponses", description: "Arrêtez de chercher, commencez à trouver—vos documents travaillent pour vous" },
                security: { title: "Sécurité de Confiance", description: "Conforme RGPD, HIPAA, SOC 2 ; chiffrement, pistes d'audit et stockage cloud isolé" },
                support: { title: "Support Toujours Actif", description: "Support standard, prioritaire et 24/7 selon votre plan" }
            }
        },
        services: {
            title: "Nos ",
            titleHighlight: "Services",
            subtext: "Support complet et services pour assurer votre succès avec Corpusio",
            list: {
                aiImpl: { title: "Implémentation IA", description: "Déploiement de solutions IA personnalisées adaptées à vos besoins, incluant formation et intégration." },
                sysInt: { title: "Intégration Système", description: "Intégration transparente avec Microsoft 365, SharePoint, Google Workspace, Dropbox, et autres." },
                analytics: { title: "Analytique & Rapports", description: "Tableaux de bord complets et rapports personnalisés pour suivre l'utilisation, le ROI et optimiser." },
                training: { title: "Formation & Support", description: "Programmes de formation de bout en bout pour votre équipe avec support technique continu." },
                security: { title: "Audit de Sécurité", description: "Évaluations de sécurité régulières, audit de conformité et mise en œuvre des meilleures pratiques." },
                customDev: { title: "Développement Sur Mesure", description: "Développement de fonctionnalités sur mesure et personnalisations pour répondre à vos besoins uniques." }
            }
        },
        products: {
            title: "Nos Produits",
            description: "Explorez nos solutions IA pour l'accès au savoir, la communication multilingue, les médias créatifs, le fitness et l'entretien véhicule",
            convoBuddy: {
                tag: "Compagnon IA",
                title: "ConvoBuddy : Chat & Voix IA Multilingue",
                description1: "ConvoBuddy est un compagnon IA multilingue qui unifie le chat IA, l'automatisation vocale et l'orchestration des flux de travail pour les entreprises.",
                description2: "Parfait pour le support client, les équipes commerciales et les flux opérationnels, assurant une communication cohérente et intelligente dans n'importe quelle langue.",
                list: [
                    "Chat & Voix IA Multilingue",
                    "Intégration WhatsApp Business",
                    "Automatisation des Messages Vocaux",
                    "Orchestration de Flux de Travail",
                    "Analyses & Insights Clients",
                    "Support Multi-canal"
                ],
                link: "En savoir plus sur ConvoBuddy →",
                demo: {
                    q1: "Quelles sont vos heures d'ouverture ?",
                    a1: "Nous sommes ouverts du lundi au vendredi, de 9h à 18h. Comment puis-je vous aider ?",
                    q2: "Pouvez-vous m'aider en espagnol ?",
                    a2: "¡Por supuesto! Puedo ayudarte en español. ¿En qué puedo asistirte?"
                }
            },
            melodyCrafters: {
                tag: "Création Musicale",
                title: "MelodyCrafters : Composition Musicale par IA",
                description1: "MelodyCrafters est une plateforme intelligente de composition musicale qui aide les artistes, producteurs et créateurs à générer des mélodies et paroles de qualité professionnelle grâce à l'IA.",
                description2: "Que vous écriviez un tube, une musique de fond ou des jingles, MelodyCrafters fournit les outils et l'inspiration dont vous avez besoin.",
                list: [
                    "Génération de mélodie par IA",
                    "Assistance écriture de paroles",
                    "Multiples genres musicaux",
                    "Suggestions de progressions d'accords",
                    "Exportation MIDI et Audio",
                    "Fonctionnalités collaboratives"
                ],
                link: "Explorer MelodyCrafters →"
            },
            happyFit: {
                tag: "Fitness & Bien-être",
                title: "HappyFit : Coach Sportif Personnel IA",
                description1: "HappyFit est une plateforme de fitness et bien-être IA qui crée des plans d'entraînement personnalisés, suit vos progrès et fournit des corrections de forme en temps réel.",
                description2: "Améliorez votre santé avec un coaching intelligent qui s'adapte à votre niveau, vos objectifs et vos préférences.",
                list: [
                    "Plans d'entraînement IA",
                    "Correction de forme en temps réel",
                    "Suivi de progression & analyses",
                    "Recommandations nutritionnelles",
                    "Défis communautaires",
                    "Intégration appareils portables"
                ],
                link: "Commencez votre parcours fitness →"
            },
            rideWithDash: {
                tag: "Entretien véhicule",
                title: "RideWithDash : compagnon IA pour véhicules sur WhatsApp et web",
                description1: "RideWithDash aide les conducteurs à gérer les questions véhicule, les diagnostics, l'historique du garage et les documents depuis WhatsApp et un espace web clair.",
                description2: "Dash transforme les symptômes et photos en vérifications guidées, plans de travail, rapports IA de véhicule et boîte à gants numérique pour immatriculation, assurance, fichiers et historique d'entretien.",
                list: [
                    "Assistant véhicule sur WhatsApp et web",
                    "Diagnostics IA et dépannage guidé",
                    "Profils garage avec fiabilité et plans de travail",
                    "Boîte à gants numérique pour fichiers et rapports IA",
                    "Historique d'entretien et rapports véhicule prêts pour le service"
                ],
                link: "Explorer RideWithDash →",
                ui: {
                    chatKicker: "WhatsApp + web",
                    chatTitle: "Chat Dash",
                    chatMeta: "Identité WhatsApp vérifiée",
                    userBubble: "Ma voiture clique mais ne démarre pas.",
                    answerTitle: "Diagnostic Dash",
                    answer: "Vérifiez la batterie, le type de clic du démarreur et les phares faibles avant de l'ajouter au plan de travail.",
                    video: "Regarder la vidéo diagnostic",
                    prompt: "Envoyez une photo à Dash ou posez une question...",
                    garageKicker: "Garage membre",
                    garageTitle: "Votre collection",
                    vehicles: [
                        { name: "2019 Ford", model: "Fiesta", plans: "3 plans", vin: "Manquant" },
                        { name: "2020 Toyota", model: "Camry", plans: "3 plans", vin: "Manquant" },
                        { name: "1988 Ford", model: "Lnt9000", plans: "0 plan", vin: "Vérifié" }
                    ],
                    vinStatus: "Statut VIN",
                    reliability: "Fiabilité",
                    gloveboxKicker: "Fichiers + rapports IA",
                    gloveboxTitle: "Boîte à gants numérique",
                    tabs: ["Fichiers importés", "Rapports IA"],
                    files: ["vehicle_report_9", "vehicle_report_8", "vehicle_report_7"],
                    reportTitle: "Rapport véhicule",
                    reportVehicle: "2019 Ford Fiesta",
                    reportStatus: "Entretien requis",
                    reportSummary: "Diagnostics ouverts, plan de travail, historique d'entretien et pièces au même endroit."
                }
            }
        },
        pricing: {
            title: "Plans ",
            titleHighlight: "Tarifaires",
            subtext: "Transparent, Flexible & Évolutif",
            period: "/mois",
            getStarted: "Commencer",
            plans: {
                standard: {
                    name: "Standard",
                    features: ["Instance CPU", "5 000 messages", "WhatsApp & OpenAI", "PDF, DOCX, Excel, images", "Réponse sous 12h"]
                },
                pro: {
                    name: "Pro",
                    features: ["CPU Évolutif", "20 000 messages", "Priorité & analyses", "Tous formats Standard", "+ mp3, wav", "Réponse sous 6h"]
                },
                enterprise: {
                    name: "Entreprise",
                    features: ["GPU NVIDIA", "Messages illimités", "IA Avancée, intégrations personnalisées", "Tous formats Pro", "+ mp4, mpg", "Réponse 4h, support dédié", "Support téléphonique 24/7"]
                }
            },
            note: "Tous les plans incluent ",
            noteHighlight1: "une sécurité de haut niveau",
            noteMiddle: " et des ",
            noteHighlight2: "Accords d'Association Commerciale",
            noteEnd: " obligatoires."
        },
        useCases: {
            title: "Cas d'",
            titleHighlight: "Usage",
            description: "Juridique, conformité, santé, ventes, RH, ingénierie, terrain—tout secteur avec un volume documentaire important et un besoin de réponses fiables.",
            stats: {
                retrieval: "récupération plus rapide",
                saved: "heures économisées par mois"
            },
            footer: "Résultats clients réels : Gain de temps majeur et satisfaction client améliorée"
        },
        security: {
            title: "Sécurité & ",
            titleHighlight: "Conformité",
            intro: "Faites confiance à Corpusio pour ",
            introHighlight: "une protection de niveau entreprise",
            features: {
                encrypted: "Stockage et transmission chiffrés",
                access: "Contrôle d'accès granulaire",
                audit: "Pistes d'audit en temps réel",
                compliance: "Conformité RGPD, HIPAA, SOC 2"
            }
        },
        faq: {
            title: "Questions Fréquemment ",
            titleHighlight: "Posées",
            subtext: "Trouvez des réponses aux questions courantes sur Corpusio",
            list: {
                q1: { q: "Qu'est-ce que Corpusio ?", a: "Corpusio est une plateforme d'intelligence documentaire alimentée par l'IA qui transforme les fichiers dispersés en réponses immédiates. Elle permet une recherche en langage naturel dans tous vos documents via WhatsApp, le web ou des applications mobiles." },
                q2: { q: "Comment fonctionne l'intégration WhatsApp ?", a: "Corpusio s'intègre parfaitement à WhatsApp Business, vous permettant d'interroger toute votre bibliothèque de documents directement via des messages WhatsApp. Envoyez simplement votre question, et notre IA récupère instantanément les informations pertinentes." },
                q3: { q: "Quels formats de fichiers sont pris en charge ?", a: "Corpusio prend en charge une large gamme de formats, notamment les PDF, documents Word, feuilles de calcul Excel, images (JPG, PNG), présentations (PPT, PPTX), et même les fichiers audio/vidéo (mp3, wav, mp4, mpg) dans les plans supérieurs." },
                q4: { q: "Mes données sont-elles sécurisées ?", a: "Absolument. Corpusio utilise un chiffrement de bout en bout, des contrôles d'accès basés sur les rôles, des pistes d'audit en temps réel et est conforme aux normes RGPD, HIPAA et SOC 2. Vos données sont stockées dans des environnements cloud isolés sans risque de clé API." },
                q5: { q: "Combien de temps prend la configuration ?", a: "La configuration est remarquablement rapide—généralement quelques minutes seulement. Connectez simplement votre stockage (Google Drive, SharePoint, Dropbox, etc.), indexez vos documents et commencez à chercher. Aucune formation spéciale ou changement d'infrastructure requis." },
                q6: { q: "Puis-je intégrer Corpusio à mes systèmes existants ?", a: "Oui ! Corpusio propose des intégrations personnalisées avec Microsoft 365, SharePoint, Google Workspace, Dropbox et d'autres systèmes d'entreprise. Notre plan Entreprise inclut une IA avancée et un support d'intégration personnalisé." },
                q7: { q: "Quel type de support proposez-vous ?", a: "Le support varie selon le plan : Standard (réponse 12h), Pro (réponse prioritaire 6h avec analyses), et Entreprise (réponse 4h avec support dédié et support téléphonique 24/7)." },
                q8: { q: "Proposez-vous un essai gratuit ?", a: "Oui, nous proposons un essai gratuit pour que vous puissiez découvrir les capacités de Corpusio par vous-même. Contactez-nous pour commencer votre essai dès aujourd'hui." }
            }
        },
        contact: {
            title: "Contactez-",
            titleHighlight: "Nous",
            subtext: "Entrez en contact avec notre équipe pour des demandes, du support ou des partenariats",
            info: {
                email: "Email",
                phone: "Téléphone",
                hours: "Heures d'Ouverture",
                hoursText: ["Lundi - Vendredi : 9:00 - 18:00 IST", "Samedi : 10:00 - 16:00 IST", "Dimanche : Fermé"]
            },
            form: {
                name: "Nom *",
                email: "Email *",
                phone: "Téléphone",
                code: "Code",
                country: "Pays",
                selectCountry: "Sélectionner Pays",
                state: "État/Région",
                selectState: "Sélectionner État",
                city: "Ville",
                selectCity: "Sélectionner Ville",
                company: "Entreprise",
                message: "Message *",
                send: "Envoyer Message",
                sending: "Envoi...",
                success: "✓ Message envoyé avec succès ! Nous vous répondrons sous 24h.",
                error: "✗ Échec de l'envoi. Veuillez réessayer ou nous envoyer un email direct.",
                fillRequired: "Veuillez remplir tous les champs obligatoires"
            }
        },
        cta: {
            title: "Commencez ",
            titleHighlight: "Maintenant",
            text: "Connectez votre stockage, indexez vos documents et commencez à chercher—le tout en quelques minutes.",
            accentText: "Aucune formation requise.",
            question: "Prêt à transformer l'accès aux connaissances de votre organisation ?",
            buttons: {
                try: "Essayer Corpusio",
                bookDemo: "Réserver une Démo",
                contact: "Contactez-nous"
            },
            footer: "Corpusio est développé par des leaders de l'industrie en IA et communications sécurisées—approuvé dans le monde entier pour transformer la façon dont les entreprises débloquent leurs connaissances."
        },
        footer: {
            description: "Libérez l'intelligence documentaire instantanée avec l'IA d'entreprise",
            products: "Produit",
            features: "Fonctionnalités",
            pricing: "Tarification",
            security: "Sécurité",
            integrations: "Intégrations",
            company: "Entreprise",
            about: "À propos",
            contact: "Contact",
            careers: "Carrières",
            blog: "Blog",
            legal: "Légal",
            privacy: "Politique de confidentialité",
            terms: "Conditions d'utilisation",
            cookie: "Politique des cookies",
            compliance: "Conformité",
            copyright: "© 2026 Corpusio. Tous droits réservés."
        }
    }
};
